// Shared "Book a demo + Get started" button pair.
//
// Both buttons render at the same height, padding, and text size so the pair
// always reads as a deliberate set.
//
// Destinations are sitewide:
//   - Book a demo  → Calendly (new tab)
//   - Get started  → app.scribenote.com/dashboard?companion (new tab)
//
// Variants:
//   - tone: "light" — for normal page backgrounds (solid indigo + outline indigo)
//           "dark"  — for dark/indigo gradient backgrounds (solid white + ghost white)
//   - size: "md" — body sections (px-7 py-3.5 text-[15px])
//           "lg" — hero / bottom CTA (px-7 py-4 text-base)
//
// The legacy `onGetStarted` / `getStartedRef` / `onGetStartedAnimationEnd`
// props are kept so existing usages (Hero's idle bounce animation) still
// compile. They're applied to the rendered anchor element when provided.
import type { Ref } from "react";

const CALENDLY_URL =
  "https://calendly.com/d/cvxs-349-5w3/companion-askcompanion-com";
const APP_URL = "https://app.scribenote.com/dashboard?companion";

type Tone = "light" | "dark";
type Size = "md" | "lg";

type CtaPairProps = {
  /** Optional click handler for analytics on top of the navigation. */
  onGetStarted?: () => void;
  tone?: Tone;
  size?: Size;
  align?: "start" | "center";
  className?: string;
  /** Anchor ref — used by the Hero CTA's 5-second idle bounce animation. */
  getStartedRef?: Ref<HTMLAnchorElement>;
  onGetStartedAnimationEnd?: (
    e: React.AnimationEvent<HTMLAnchorElement>,
  ) => void;
};

const SIZE_CLASSES: Record<Size, string> = {
  md: "w-full sm:w-auto px-7 py-3.5 text-[15px]",
  lg: "w-full sm:w-auto px-7 py-4 text-base",
};

const LIGHT_PRIMARY =
  "cursor-pointer rounded-full border-0 bg-indigo-500 font-body font-semibold text-white shadow-[0_12px_28px_-8px_rgba(99,102,241,0.45)] transition-[transform,box-shadow,background] duration-150 ease-[ease] hover:-translate-y-px hover:bg-indigo-600 hover:shadow-[0_18px_40px_-8px_rgba(99,102,241,0.55)]";
const LIGHT_SECONDARY =
  "rounded-full border border-indigo-200 bg-white font-body font-semibold text-indigo-700 no-underline transition-[background,transform] duration-150 ease-[ease] hover:-translate-y-px hover:bg-indigo-50";

const DARK_PRIMARY =
  "cursor-pointer rounded-full border-0 bg-white font-body font-semibold text-indigo-700 shadow-[0_12px_32px_-8px_rgba(49,46,129,0.35)] transition-[transform,box-shadow] duration-150 ease-[ease] hover:-translate-y-px hover:shadow-[0_18px_40px_-8px_rgba(49,46,129,0.45)]";
const DARK_SECONDARY =
  "rounded-full border border-white/45 bg-white/10 font-body font-semibold text-white no-underline backdrop-blur-md transition-[background,transform] duration-150 ease-[ease] hover:-translate-y-px hover:bg-white/20";

const ARROW = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12h14M13 5l7 7-7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function CtaPair({
  onGetStarted,
  tone = "light",
  size = "md",
  align = "start",
  className = "",
  getStartedRef,
  onGetStartedAnimationEnd,
}: CtaPairProps) {
  const sizeClass = SIZE_CLASSES[size];
  const primary = tone === "dark" ? DARK_PRIMARY : LIGHT_PRIMARY;
  const secondary = tone === "dark" ? DARK_SECONDARY : LIGHT_SECONDARY;
  const justify = align === "center" ? "justify-center" : "justify-start";
  const ctaClass = `inline-flex items-center justify-center gap-2 no-underline ${primary} ${sizeClass}`;

  return (
    <div
      className={`flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center ${justify} ${className}`}
    >
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center gap-2 ${secondary} ${sizeClass}`}
      >
        Book a demo
      </a>
      <a
        ref={getStartedRef}
        href={APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onGetStarted}
        onAnimationEnd={onGetStartedAnimationEnd}
        className={ctaClass}
      >
        Get started
        {ARROW}
      </a>
    </div>
  );
}
